import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import Modal from "../../layouts/Modal";
import { IInvoice, IInvoiceItem } from "../../models/invoice/IInvoice";
import { DialogTitle } from "@headlessui/react";
import { TextInput } from "../shared/Inputs/TextInput";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import useVatMarginScheme from "../../data/useVatMarginScheme";

interface InvoiceItemModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  invoice: IInvoice | null;
}

export default function InvoiceItemModal({ open, setOpen, invoice }: InvoiceItemModalProps) {
  const { t } = useTranslation("accounts");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Invoice Item"));
  const { standardRate } = useVatMarginScheme();
  const { register, setValue, getValues, watch, reset, handleSubmit } = useForm<IInvoiceItem>({
    defaultValues: {
      unitPrice: 0,
      quantity: 1,
      taxRate: 20,
      accountCode: "",
      description: "",
      notes: "",
    },
  });
  const { createItem } = useInvoiceMutations();

  useEffect(() => {
    if (open) setValue("taxRate", standardRate);
  }, [open, standardRate, setValue]);

  const onSubmit = (data: any) => {
    const payload: IInvoiceItem = {
      ...data,
      invoiceId: invoice?.id || "",
      partId: null, // Assuming no part is selected
      deleted: null,
      taxRate: parseFloat(data.taxRate) || standardRate,
    };

    createItem.mutate(payload, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        reset();
        setOpen(false);
      },
      onError: (error) => {
        saveHandlers.onError();
      },
    });
  }

  return (
    <Modal open={open} setOpen={setOpen} width="sm:max-w-xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle as="h3" className="text-lg font-medium leading-6 text-gray-900">{t("Invoice Item")}</DialogTitle>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div className="sm:col-span-2">
            <TextInput
              label={t("Description")}
              register={register}
              registerName={"description"}
              placeholder={t("Enter item description")}
            />
          </div>

          <TextInput
            label={t("Unit Price")}
            register={register}
            registerName={"unitPrice"}
            placeholder={t("Enter unit price")}
            type="number"
          />
          <TextInput
            label={t("Quantity")}
            register={register}
            registerName={"quantity"}
            placeholder={t("Enter quantity")}
            type="number"
          />
          <TextInput
            label={t("Tax Rate")}
            register={register}
            registerName={"taxRate"}
            placeholder={t("Enter tax rate")}
            type="number"
          />
          <TextInput
            label={t("Account Code")}
            register={register}
            registerName={"accountCode"}
            placeholder={t("Enter account code")}
          />

          <div className="sm:col-span-2">
            <TextInput
              label={t("Notes")}
              register={register}
              registerName={"notes"}
              placeholder={t("Enter item notes")}
              richText={true}
            />
          </div>
        </div>
        <ButtonInput
          isSubmit={true}
          label={t("Save Item")}
          classes="mt-4 float-right"
          onClick={() => {

          }}
        />
      </form>
    </Modal>
  )
}